import clsx from "clsx";
import { ArrowDown, ArrowUp, Plus, Search, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { TextField } from "../../web/components/fields";
import { AddPicker } from "../../web/components/AddPicker";
import type { SyncOption } from "../../web/components/SyncSelect";
import { Badge, Box, ErrorNotice, IconButton } from "../../web/components/ui";
import { WindowPopup } from "../../web/components/WindowPopup";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { basisLabel, fromNet, rateFor, toNet, toNumber } from "../../shared/money";
import { plural } from "../../shared/words";
import { bundleBreakdown, type BundleTotals, bundleTotals } from "./bundleTotals";
import { matchesSearch, NeedsAttention } from "./ListToolbar";
import { can, itemName, itemTax } from "./data";
import { underMinimum } from "./pricing";
import { duration } from "./describe";
import { BUNDLE_MAX_ITEMS, type BundleLine, inUse, isHourlyLabour, isProduct, type MaterialRow, PACKAGE_MAX_PARTS, type PriceListData } from "./meta";
import { HiddenMark, retiredName } from "./HiddenMark";
import { type ItemProblem, type ItemProblemId, bundleItemAttention, itemContext, tintsRow } from "./attention";
import { OnlyPill, problemKinds, useLiveOnly, withProblem } from "./ProblemChips";

/*
 * What's in a bundle, or what an item's built from (PackageCards.tsx): its lines as a table to price it by, and what it makes.
 */

/**
 * What a bundle can take: items for sale (not hidden ones: using the bundle would put one back on jobs); what an item's built from
 * (`products`): products in use (catalogue.ts).
 */
export function itemChoices(data: PriceListData, except: readonly string[], products: boolean): SyncOption[] {
    return data.materials
        .filter((m) => (products ? inUse(m) : m.active) && !except.includes(m.uuid) && isProduct(m) === products)
        .map((m) => ({
            value: m.uuid,
            label: itemName(data, m),
            detail: [m.item_number, m.hidden ? "hidden" : ""].filter(Boolean).join(" · ") || undefined,
        }));
}

/** What Contents lists: a bundle's items, or the products an item's built from (PackageCards.tsx), and how they're changed. */
export interface ContentsOf {
    kind: "bundle" | "package";
    uuid: string;
    name: string;
    lines: readonly BundleLine[];
    /** May change them (its permission). */
    canEdit: boolean;
    save: (lines: BundleLine[]) => Promise<unknown>;
    /** Being changed as a draft: its save bar, along the bottom of the list (tinted while it shows). */
    saveBar?: ReactNode;
}

/**
 * What's in it, as a table to price it by: each item, how many, its price (each, and for the line) and, with See costs, its cost
 * and markup, then what they add up to. A bundle has no price of its own (ServiceM8 adds each item at its own price), and a
 * package's is what its parts add up to, so either's price is changed by its items', each in its own window: that changes the item
 * everywhere, so it's not done here, where it'd look like this one's price.
 * Amounts are in the business's usual terms (with tax, or without), whatever each item's own.
 */
export function Contents({
    data,
    of,
    onOpenItem,
    only,
    onOnly,
}: Readonly<{
    data: PriceListData;
    of: ContentsOf;
    onOpenItem: (uuid: string) => void;
    /** Only the items with this kind of warning (its chip pressed; cleared here), or null: all. */
    only: ItemProblemId | null;
    onOnly: (only: ItemProblemId | null) => void;
}>) {
    const fmt = useMoney();
    const { money } = useCompany();
    const [error, setError] = useState<Error | null>(null);
    const byUuid = new Map(data.materials.map((m) => [m.uuid, m]));
    const lines = of.lines;
    const totals = bundleTotals(data, lines);
    const { canEdit } = of;
    const pkg = of.kind === "package";
    // What its lines are called: a bundle's items, a built item's products.
    const word = pkg ? "product" : "item";
    const max = pkg ? PACKAGE_MAX_PARTS : BUNDLE_MAX_ITEMS;
    const editable = canEdit;
    const seesCosts = can(data, "costs.view");
    const inc = money.amountsIncludeTax;
    // Its usual tax by name ("inc GST"), as every amount is labelled.
    const terms = basisLabel(inc, rateFor("", data.taxRates));
    const save = async (material_list: BundleLine[]) => {
        setError(null);
        try {
            return await of.save(material_list);
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };
    const move = (from: number, to: number) => {
        const next = [...lines];
        const [moved] = next.splice(from, 1);
        if (moved) next.splice(to, 0, moved);
        save(next).catch(() => undefined);
    };
    // An item's amount (its price or cost, in its own terms) in the business's usual terms.
    const shown = (m: MaterialRow, amount: string) => {
        const { tax } = itemTax(data, m);
        return fromNet(toNet(toNumber(amount), m.price_includes_taxes, tax), inc, tax);
    };
    const otherBundles = (uuid: string) => data.bundles.filter((x) => x.active && x.uuid !== of.uuid && x.material_list.some((l) => l.uuid === uuid)).length;
    // A line's labour, in minutes: an hourly rate's quantity is hours. Null: not labour.
    // ...a labour time's (labourTime.ts) is so many of its time.
    const labourMinutes = (m: MaterialRow, quantity: number) => {
        if (isHourlyLabour(m.name)) return quantity * 60;
        const link = data.itemPricing[m.uuid]?.labour;
        return link ? link.minutes * quantity : null;
    };
    // Its labour, all told.
    const labourTotal = lines.reduce((sum, l) => {
        const m = byUuid.get(l.uuid);
        return sum + ((m && labourMinutes(m, l.quantity)) ?? 0);
    }, 0);
    const [adding, setAdding] = useState(false);
    // Its items' warnings (attention.ts bundleItemAttention), to mark their rows.
    const attention = bundleItemAttention({ material_list: [...lines] }, itemContext(data), seesCosts);
    // Searching its items (name, item number, full name).
    const [filter, setFilter] = useState("");
    // Its lines (with where each is, to move it) that the search finds, of those with the warning it's narrowed to (if it is).
    const nameOf = (uuid: string) => {
        const m = byUuid.get(uuid);
        return m ? itemName(data, m) : "An item";
    };
    // Narrowed to a kind no item has any more: the whole list again.
    const narrowTo = useLiveOnly(only, attention, onOnly);
    const found = lines
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => {
            const m = byUuid.get(l.uuid);
            if (!withProblem(attention, l.uuid, narrowTo)) return false;
            return !m || matchesSearch(`${itemName(data, m)} ${m.item_number} ${m.name}`, filter);
        });
    const narrowed = filter.trim() !== "" || narrowTo !== null;
    // The columns, the list's header and total row lined up with its rows: how many (first, as a quote's line reads: "2 x Deadbolt"),
    // the item, its price, then moving or removing it. What it costs and makes: its breakdown (BundleProfitBox), and each item's in
    // its own window.
    const cols = clsx("grid items-center gap-x-2.5 px-4", editable ? "grid-cols-[3rem_minmax(0,1fr)_5.25rem_3rem]" : "grid-cols-[3rem_minmax(0,1fr)_5.25rem]");

    return (
        <>
            <ErrorNotice error={error} />
            {/* Searching its items, and adding some (a popup in the window: a search with an Add on each), as a group's items. */}
            <Box>
                <div className="flex items-center gap-2">
                    <label className="relative block min-w-0 flex-1">
                        <span className="sr-only">Search its {word}s</span>
                        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                        <input className="input pl-9" placeholder={`Search its ${word}s`} value={filter} onChange={(e) => setFilter(e.target.value)} />
                    </label>
                    {editable ? (
                        <button
                            type="button"
                            className="btn-secondary shrink-0"
                            disabled={lines.length >= max}
                            title={(() => {
                                if (lines.length >= max) return pkg ? `An item is linked to at most ${max} products` : `A bundle holds at most ${max} items`;
                                return pkg ? "Its price is what its products add up to. Only the item goes on jobs." : "ServiceM8 adds each item to the job at its own price, under the bundle's name";
                            })()}
                            onClick={() => setAdding(true)}
                        >
                            <Plus className="size-4" /> Add {word}
                        </button>
                    ) : null}
                </div>
            </Box>
            {adding ? (
                <WindowPopup top title={`Add ${word}s to ${of.name || (pkg ? "the item" : "the bundle")}`} onClose={() => setAdding(false)}>
                    <AddPicker
                        inline
                        placeholder="Search by name or item number"
                        options={itemChoices(
                            data,
                            lines.map((l) => l.uuid),
                            pkg,
                        )}
                        onAdd={(uuid) => save([...lines, { uuid, quantity: 1 }])}
                        onUndo={(uuid) => save(lines.filter((l) => l.uuid !== uuid))}
                    />
                </WindowPopup>
            ) : null}
            <Box className="py-0">
                <div className={clsx(cols, "-mx-4 h-11 border-b border-line text-xs text-muted")}>
                    <span className="text-center">Qty</span>
                    <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">
                            {narrowed ? `${found.length} of ${plural(lines.length, word)}` : plural(lines.length, word)}
                            {/* Its labour, all told (hourly rates' hours). */}
                            {labourTotal ? ` · ${duration(Math.round(labourTotal))} labour` : null}
                        </span>
                        {/* Narrowed to one kind of warning (its chip): which, cleared with its × (ProblemChips.tsx, as every such list). */}
                        <OnlyPill only={narrowTo} kinds={problemKinds(attention, nameOf)} onClear={() => onOnly(null)} />
                    </span>
                    <span className="text-right">Price</span>
                </div>
                {found.length === 0 ? <p className="m-0 py-3 text-sm text-muted">{lines.length ? `No ${word}s in it match.` : `No ${word}s yet.`}</p> : null}
                <ul className="-mx-4 divide-y divide-line">
                    {found.map(({ l, i }) => {
                        const m = byUuid.get(l.uuid);
                        return (
                            <ContentRow
                                key={l.uuid}
                                data={data}
                                line={l}
                                item={m}
                                cols={cols}
                                editable={editable}
                                problems={attention.get(l.uuid) ?? []}
                                each={m ? shown(m, m.price) : 0}
                                others={m ? otherBundles(m.uuid) : 0}
                                labour={m ? labourMinutes(m, l.quantity) : null}
                                first={i === 0}
                                last={i === lines.length - 1}
                                // A bundle needs an item (ServiceM8); a built item can be left with none while it's made.
                                only={!pkg && lines.length === 1}
                                // A draft (a built item's): what's typed counts at once, so holding its Save straight after typing saves it.
                                live={pkg}
                                onQuantity={(quantity) => save(lines.map((x) => (x.uuid === l.uuid ? { ...x, quantity } : x)))}
                                onOpen={() => onOpenItem(l.uuid)}
                                onMove={(by) => move(i, i + by)}
                                onRemove={() => save(lines.filter((x) => x.uuid !== l.uuid)).catch(() => undefined)}
                            />
                        );
                    })}
                </ul>
                {/* What its prices add up to (all of it, whatever's searched): what the customer pays. What it makes: its breakdown, below. */}
                <div className={clsx(cols, "-mx-4 border-t border-line py-2.5 font-semibold")}>
                    <span className="col-span-2 min-w-0">
                        Total <span className="text-xs font-normal text-muted">({terms})</span>
                    </span>
                    <span className="text-right tabular-nums">{fmt.money(inc ? totals.priceWithTax : totals.priceNet)}</span>
                </div>
                {totals.missing ? <p className="m-0 py-2 text-xs text-danger">{plural(totals.missing, word)} in it no longer exist and are left out.</p> : null}
                {of.saveBar ? <div className="-mx-4 border-t border-brand-500/30 bg-brand-500/5 px-4 py-2">{of.saveBar}</div> : null}
            </Box>
            {/* At the bottom (of the window when there's room), as an item's: what it makes, its parts and its labour. */}
            <BundleProfitBox data={data} lines={lines} seesCosts={seesCosts} per={of.kind} />
        </>
    );
}

/**
 * One item in a bundle's contents: how many (typed), the item (its name opens it; hidden, archived
 * or what needs attention marked; its labour's time), its line's price (the price of one under it, when more than one), then (`editable`) moving or removing it.
 * Its price is its own: changed in its window (everywhere it's sold), not here.
 */
function ContentRow(
    props: Readonly<{
        data: PriceListData;
        line: BundleLine;
        /** The item (none: it no longer exists). */
        item: MaterialRow | undefined;
        cols: string;
        editable: boolean;
        problems: readonly ItemProblem[];
        /** The price of one, in the business's usual terms. */
        each: number;
        /** Other active bundles holding it. */
        others: number;
        /** Its labour, in minutes (null: not labour). */
        labour: number | null;
        first: boolean;
        last: boolean;
        /** The only item in it (a bundle needs one). */
        only: boolean;
        /** Its quantity counted as typed (a draft's), not when left. */
        live: boolean;
        onQuantity: (quantity: number) => Promise<unknown>;
        onOpen: () => void;
        onMove: (by: -1 | 1) => void;
        onRemove: () => void;
    }>,
) {
    const { data, line: l, item: m, problems } = props;
    const fmt = useMoney();
    const danger = problems.some((p) => p.danger);
    let tint: string | undefined;
    if (danger) tint = "bg-danger-soft";
    else if (problems.some(tintsRow)) tint = "bg-warning-soft";
    // Its labour's time (the badge): an hourly rate's quantity is hours; a labour time's, so many of its time.
    const labourTitle =
        m && isHourlyLabour(m.name) ? `Labour: ${l.quantity} hr at its hourly rate` : `Labour time: ${l.quantity} × ${duration(m ? (data.itemPricing[m.uuid]?.labour?.minutes ?? 0) : 0)}`;
    return (
        <li className={clsx(props.cols, "py-2.5", tint)}>
            <TextField
                label="Quantity"
                hideLabel
                // Centred in its narrow box, as the column heading is.
                className="[&_input]:text-center"
                // To 4 places: a share of an hour (20 min of labour, 1/3) reads 0.3333, kept exactly unless typed over.
                value={String(Math.round(l.quantity * 10_000) / 10_000)}
                inputMode="decimal"
                disabled={!props.editable}
                live={props.live}
                validate={(v) => (Number(v) > 0 ? null : "Above 0")}
                onSave={(v) => props.onQuantity(Number(v))}
            />
            <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-2">
                    {m ? <HiddenMark item={m} /> : null}
                    <button type="button" className={clsx("link-button truncate text-left font-medium", m && retiredName(m))} title={m ? itemName(data, m) : undefined} disabled={!m} onClick={props.onOpen}>
                        {m ? itemName(data, m) : "An item that no longer exists"}
                    </button>
                    {/* Anything to look at on it (every warning it has), as the Items list marks it: open it to deal with it. */}
                    <NeedsAttention problems={problems.map((p) => p.text)} danger={danger} />
                    {/* Labour: its time (its quantity, typed in minutes, is hours of its hourly rate). */}
                    {props.labour === null ? null : (
                        <Badge tone="blue" title={labourTitle}>
                            {duration(Math.round(props.labour))}
                        </Badge>
                    )}
                </span>
                <span className="block truncate text-xs text-muted">{[m?.item_number, props.others ? `also in ${plural(props.others, "other bundle")}` : null].filter(Boolean).join(" · ")}</span>
            </span>
            <span className="text-right">
                <span className="tabular-nums" title={m ? "Its price wherever it's sold: open it to change it" : undefined}>
                    {m ? fmt.money(props.each * l.quantity) : "-"}
                </span>
                {m && l.quantity !== 1 ? <span className="block text-xs whitespace-nowrap text-muted tabular-nums">{fmt.money(props.each)} each</span> : null}
            </span>
            {props.editable ? (
                <span className="flex items-center justify-end gap-1">
                    {/* Up and down stacked, to leave the name room. */}
                    <span className="-my-1 flex flex-col">
                        <IconButton label="Move up" disabled={props.first} onClick={() => props.onMove(-1)}>
                            <ArrowUp className="size-3.5" />
                        </IconButton>
                        <IconButton label="Move down" disabled={props.last} onClick={() => props.onMove(1)}>
                            <ArrowDown className="size-3.5" />
                        </IconButton>
                    </span>
                    <IconButton label="Remove" disabled={props.only} onClick={props.onRemove}>
                        <Trash2 className="size-4" />
                    </IconButton>
                </span>
            ) : null}
        </li>
    );
}

/** A figure's cell, as an item's ProfitBox has them: on the right, its digits lined up. */
const FIGURE = "py-0.5 pl-4 text-right tabular-nums";

/**
 * What a bundle makes, before tax, as an item's ProfitBox shows it: a small table of its parts, its labour (bundleTotals.ts
 * isLabourItem) and the two together (just the total when it's all one or the other). Price, cost, profit and markup (with See costs;
 * else its price only). An item in a row with no cost: that row's profit and markup can't be told ("-"). Under the minimum markup:
 * red, as an item's.
 */
function BundleProfitBox({ data, lines, seesCosts, per }: Readonly<{ data: PriceListData; lines: readonly BundleLine[]; seesCosts: boolean; per: "bundle" | "package" }>) {
    const fmt = useMoney();
    const { parts, labour } = bundleBreakdown(data, lines);
    const all = bundleTotals(data, lines);
    const title = `Per ${per} (${basisLabel(false, rateFor("", data.taxRates))})`;
    const both = parts.priceNet + parts.costNet > 0 && labour.priceNet + labour.costNet > 0;
    const rows: [string, BundleTotals][] = both
        ? [
              ["Parts", parts],
              ["Labour", labour],
              ["Total", all],
          ]
        : [["Total", all]];
    const headings = seesCosts ? ["Price", "Cost", "Profit", "Markup"] : ["Price"];
    return (
        <Box shaded className="mt-auto">
            <table className="w-full border-collapse">
                <thead>
                    <tr>
                        <th scope="col" className="box-title text-left whitespace-nowrap">
                            {title}
                        </th>
                        {headings.map((h) => (
                            <th key={h} scope="col" className={clsx(FIGURE, "text-xs font-normal text-muted")}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(([label, t]) => {
                        // Its profit and markup: none to tell with an item in it that has no cost (it'd be far too high).
                        const known = t.costNet > 0 && !t.noCost;
                        const profit = t.priceNet - t.costNet;
                        const markup = known ? (profit / t.costNet) * 100 : null;
                        const low = underMinimum(markup, data.settings.minMarkup);
                        const verb = t.noCost > 1 ? "have" : "has";
                        const why = known ? undefined : `${plural(t.noCost || 1, "item")} in it ${verb} no cost`;
                        // Its price; with See costs, its cost, profit and markup too ("-": can't be told).
                        const figures = [fmt.money(t.priceNet)];
                        if (seesCosts) figures.push(fmt.money(t.costNet), known ? fmt.money(profit) : "-", markup === null ? "-" : fmt.percent(Math.round(markup)));
                        return (
                            <tr key={label} className={label === "Total" ? "font-semibold" : undefined}>
                                <th scope="row" className="py-0.5 text-left text-xs font-normal text-muted">
                                    {label}
                                </th>
                                {figures.map((f, i) => (
                                    <td key={headings[i]} className={clsx(FIGURE, low && i >= 2 && "text-danger")} title={i >= 2 ? why : undefined}>
                                        {f}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Box>
    );
}
